import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Row, Col, Table, Input, Button, DatePicker,
  Modal, Typography, Tag, Spin, message as antdMessage,
} from "antd";
import {
  SearchOutlined, PlayCircleOutlined,
  DollarOutlined, UserOutlined, BankOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { postReq } from "../api";
const { Text, Title } = Typography;

/* ── currency formatter ──────────────────────── */
const formatPrice = (val) => {
  if (!val && val !== 0) return "—";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

/* ── summary card ────────────────────────────── */
function SummaryCard({ label, amount, color = "#3699FF", bg = "#f0f7ff" }) {
  return (
    <Card
      size="small"
      style={{
        // borderRadius: 12,
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
        height: "100%",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        // borderLeft: `4px solid ${color}`,
      }}
      bodyStyle={{ padding: "16px 20px" }}
      className="payroll-card"
    >
      <Text type="secondary" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
        {label}
      </Text>
      <div style={{ fontSize: 20, fontWeight: 700, color: "#1e293b", marginTop: 8 }}>
        {formatPrice(amount)}
      </div>
    </Card>
  );
}

const SUMMARY_CARDS = [
  { key: "grossPayable", label: "Gross", bg: "#f0f7ff", color: "#3699FF" },
  { key: "netPayable", label: "Net", bg: "#e9f9ee", color: "#27AE60" },
  { key: "earnings.basic", label: "Basic", bg: "#fff8e1", color: "#F59E0B" },
  { key: "earnings.hra", label: "HRA", bg: "#f3e8ff", color: "#8B5CF6" },
  { key: "earnings.specialAllowance", label: "Allowances", bg: "#fce8f3", color: "#EC4899" },
  { key: "esic", label: "ESIC", bg: "#feecec", color: "#E74C3C" },
  { key: "pf", label: "PF", bg: "#e8f0fe", color: "#13487D" },
];

/* ── columns ─────────────────────────────────── */
const buildColumns = () => [
  {
    title: "Employee",
    key: "employee",
    fixed: "left",
    width: 170,
    render: (_, row) => (
      <div>
        <Text strong style={{ color: "#13487D", display: "block" }}>
          {row.userId?.name}
        </Text>
      </div>
    ),
  },
  {
    title: "CTC",
    key: "ctc",
    width: 110,
    render: (_, row) => formatPrice(row.salarySnapshot?.monthlyCTC),
  },
  {
    title: "Days",
    key: "days",
    width: 70,
    align: "center",
    render: (_, row) => (
      <Tag color="blue">{row.attendanceSnapshot?.totalWorkingDays ?? "—"}</Tag>
    ),
  },
  {
    title: "Payable Days",
    key: "payableDays",
    width: 110,
    align: "center",
    render: (_, row) => (
      <Tag color="geekblue">{row.attendanceSnapshot?.payableDays ?? "—"}</Tag>
    ),
  },
  {
    title: "Gross",
    key: "gross",
    width: 110,
    render: (_, row) => formatPrice(row.grossPayable),
  },
  {
    title: "Basic",
    key: "basic",
    width: 100,
    render: (_, row) => formatPrice(row.earnings?.basic),
  },
  {
    title: "HRA",
    key: "hra",
    width: 100,
    render: (_, row) => formatPrice(row.earnings?.hra),
  },
  {
    title: "Allowance",
    key: "allowance",
    width: 110,
    render: (_, row) => formatPrice(row.earnings?.specialAllowance),
  },
  {
    title: "Tax",
    key: "tax",
    width: 90,
    render: (_, row) => formatPrice(row.deductions?.professionalTax),
  },
  {
    title: "PF Employer",
    key: "pfEmployer",
    width: 110,
    render: (_, row) => formatPrice(row.deductions?.pfEmployer),
  },
  {
    title: "PF Employee",
    key: "pfEmployee",
    width: 110,
    render: (_, row) => formatPrice(row.deductions?.pfEmployee),
  },
  {
    title: "ESIC Employer",
    key: "esicEmployer",
    width: 120,
    render: (_, row) => formatPrice(row.deductions?.esicEmployer),
  },
  {
    title: "ESIC Employee",
    key: "esicEmployee",
    width: 120,
    render: (_, row) => formatPrice(row.deductions?.esicEmployee),
  },
  {
    title: "Net Payable",
    key: "netPayable",
    fixed: "right",
    width: 120,
    render: (_, row) => (
      <Text strong style={{ color: "#27AE60" }}>
        {formatPrice(row.netPayable)}
      </Text>
    ),
  },
];

const DEFAULT_SUMMARY = {
  grossPayable: 0,
  netPayable: 0,
  earnings: { basic: 0, hra: 0, specialAllowance: 0 },
  deductions: { professionalTax: 0, pfEmployee: 0, pfEmployer: 0, esicEmployee: 0, esicEmployer: 0 },
};

/* ── component ───────────────────────────────── */
export default function PayrollTable() {
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, "month"));
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(DEFAULT_SUMMARY);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });

  // Run payroll modal
  const [modalOpen, setModalOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [running, setRunning] = useState(false);
  const payrollMonth = moment().subtract(1, "month");

  /* ── fetch ─────────────────────────────────── */
  const fetchData = useCallback(async (pg = pagination, q = search, month = selectedMonth) => {
    setLoading(true);
    try {
      const data = await postReq("/user/getMonthlyPayrollList", {
        page: pg.page,
        pageSize: pg.pageSize,
        search: q,
        month: month.format("M"),
        year: month.format("YYYY"),
      });

      if (data.status === 200) {
        setRows(data.data.data);
        setTotal(data.data.total);
        setSummary(data.data.summary || DEFAULT_SUMMARY);
      } else {
        antdMessage.error(data.message || "Failed to fetch payroll data");
        setRows([]);
        setTotal(0);
        setSummary(DEFAULT_SUMMARY);
      }
    } catch (err) {
      antdMessage.error(err?.message || "Error fetching payroll data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(pagination, search, selectedMonth);
  }, [pagination, search, selectedMonth]);

  /* ── run payroll ────────────────────────────── */
  const handleRunPayroll = async () => {
    setRunning(true);
    try {
      const data = await postReq("/user/run", {
        month: payrollMonth.format("M"),
        year: payrollMonth.format("YYYY"),
        password,
      });

      if (data.status === 200) {
        antdMessage.success("Payroll generated successfully");
        fetchData(pagination, search, selectedMonth);
        setModalOpen(false);
      } else {
        antdMessage.error(data.message || "Error running payroll");
      }
    } catch (err) {
      antdMessage.error(err?.message || "Error");
    } finally {
      setRunning(false);
      setPassword("");
    }
  };

  /* ── summary value resolver ─────────────────── */
  const getSummaryValue = (key) => {
    if (key === "esic")
      return (summary.deductions?.esicEmployee || 0) + (summary.deductions?.esicEmployer || 0);
    if (key === "pf")
      return (summary.deductions?.pfEmployee || 0) + (summary.deductions?.pfEmployer || 0);
    const parts = key.split(".");
    if (parts.length === 2) return summary[parts[0]]?.[parts[1]] || 0;
    return summary[key] || 0;
  };

  /* ── render ─────────────────────────────────── */
  return (
    <div style={{ padding: "4px 5px", background: "#f8fafc", minHeight: "100vh" }}>

      {/* Summary Cards Grid */}


      {/* Controls & Table Card */}
      <Card
        style={{
          borderRadius: 12,
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02)"
        }}
        bodyStyle={{ padding: "24px" }}
      >

        {/* Controls */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "end",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20
        }}>
          {/* <Input
            prefix={<SearchOutlined style={{ color: "#94a3b8", marginLeft: "10px" }} />}
            placeholder="Search employee name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 260, borderRadius: 8, borderColor: "#cbd5e1" }}
          /> */}
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <DatePicker
              picker="month"
              value={selectedMonth}
              disabledDate={(d) => d && d.isAfter(moment().subtract(1, "month"), "month")}
              onChange={(val) => {
                if (val) {
                  setSelectedMonth(val);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }
              }}
              format="MMM YYYY"
              allowClear={false}
              style={{ width: 160, borderRadius: 8, borderColor: "#cbd5e1", height: 38 }}
            />
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => setModalOpen(true)}
              style={{
                background: "#13487D",
                borderColor: "#13487D",
                borderRadius: 8,
                height: 38,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: 6
              }}
            >
              Run Payroll
            </Button>
          </div>
        </div>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 16,
          marginBottom: 24
        }}>
          {SUMMARY_CARDS.map((card) => (
            <SummaryCard
              key={card.key}
              label={card.label}
              amount={getSummaryValue(card.key)}
              color={card.color}
              bg={card.bg}
            />
          ))}
        </div>

        {/* Table */}
        <Table
          dataSource={rows}
          columns={buildColumns()}
          rowKey={(r) => r._id}
          loading={loading}
          size="middle"
          scroll={{ x: 1400 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20"],
            showTotal: (t) => `Total ${t} records`,
            onChange: (page, pageSize) => setPagination({ page, pageSize }),
            style: { padding: "12px 20px" },
          }}
          rowClassName={(_, i) => i % 2 !== 0 ? "payroll-row-alt" : ""}
        />
      </Card>

      {/* Run Payroll Modal */}
      <Modal
        open={modalOpen}
        onCancel={() => !running && setModalOpen(false)}
        maskClosable={!running}
        closable={!running}
        keyboard={!running}
        title={
          <span style={{ color: "#13487D", fontWeight: 700, fontSize: 16 }}>Run Payroll Confirmation</span>
        }
        footer={
          running ? null : [
            <Button key="cancel" onClick={() => setModalOpen(false)} style={{ borderRadius: 6 }}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              disabled={!password}
              onClick={handleRunPayroll}
              style={{ background: "#13487D", borderColor: "#13487D", borderRadius: 6 }}
            >
              Confirm & Run
            </Button>,
          ]
        }
        width={460}
        destroyOnClose
      >
        {running ? (
          <div style={{
            padding: "40px 0", display: "flex", flexDirection: "column",
            alignItems: "center", gap: 16,
          }}>
            <Spin size="large" />
            <Text type="secondary">Generating payroll records, please wait...</Text>
          </div>
        ) : (
          <div style={{ paddingTop: 10 }}>
            <div style={{ marginBottom: 16 }}>
              <Text style={{ fontSize: 14, display: "block" }}>
                Are you sure you want to run payroll for{" "}
                <Text strong style={{ color: "#13487D" }}>{payrollMonth.format("MMM YYYY")}</Text>?
              </Text>
            </div>
            <div style={{
              background: "#fffbeb",
              border: "1px solid #fef3c7",
              borderRadius: 8,
              padding: "12px 16px",
              marginBottom: 20
            }}>
              <Text type="warning" style={{ fontSize: 13, color: "#b45309" }}>
                ⚠️ Please review the attendance summary and salary structures carefully.
                Once processed, payroll entries cannot be revised or rolled back.
              </Text>
            </div>
            <Input.Password
              placeholder="Enter administrator password to confirm"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ borderRadius: 8, height: 38 }}
            />
          </div>
        )}
      </Modal>

      <style>{`
        .payroll-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05) !important;
        }
        .payroll-row-alt td {
          background: #f8fafc !important;
        }
        .ant-table-thead > tr > th {
          background: #f8fafc !important;
          color: #475569 !important;
          font-weight: 600 !important;
          border-bottom: 2px solid #e2e8f0 !important;
        }
        .ant-table-cell-fix-left,
        .ant-table-cell-fix-right {
          background: #ffffff !important;
        }
        .payroll-row-alt .ant-table-cell-fix-left,
        .payroll-row-alt .ant-table-cell-fix-right {
          background: #f8fafc !important;
        }
        .ant-table-row:hover td {
          background: #f1f5f9 !important;
        }
        .ant-table-row:hover .ant-table-cell-fix-left,
        .ant-table-row:hover .ant-table-cell-fix-right {
          background: #f1f5f9 !important;
        }
      `}</style>
    </div>
  );
}
